import React, { useState, useEffect, useCallback } from 'react';
import { leaveService } from '../services/leaveService';
import './LeaveCalendar.css';

const LeaveCalendar = ({ employeeId = null }) => {
  const [leaves, setLeaves] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    leaveType: 'ALL',
    status: 'ALL',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.leaveType !== 'ALL') params.leaveType = filters.leaveType;
      if (filters.status !== 'ALL') params.status = filters.status;

      if (employeeId) params.employeeId = employeeId;
      const response = await leaveService.getCalendar(params);
      const data = response.data || [];
      const filtered = employeeId ? data.filter((l) => l.employeeId === employeeId) : data;
      setLeaves(filtered);
    } catch (err) {
      setError('Erreur lors du chargement du calendrier');
      console.error('Error loading calendar:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, employeeId]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const handleExportExcel = async () => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Vous devez être connecté pour exporter');
        return;
      }

      const params = filters.status !== 'ALL' ? `?status=${filters.status}` : '';
      const url = `http://localhost:8098/api/leaves/export/excel${params}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const blob = await response.blob();
      
      // Vérifier que le blob n'est pas vide
      if (blob.size === 0) {
        throw new Error('Le fichier exporté est vide');
      }

      // Vérifier le type MIME
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('excel') && !contentType.includes('spreadsheet') && !contentType.includes('octet-stream')) {
        console.warn('Type MIME inattendu:', contentType);
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `conges_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      // Attendre un peu avant de nettoyer
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de l\'export Excel';
      setError(errorMessage);
      console.error('Error exporting to Excel:', err);
    }
  };

  const handleExportPDF = async () => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Vous devez être connecté pour exporter');
        return;
      }

      const params = filters.status !== 'ALL' ? `?status=${filters.status}` : '';
      const url = `http://localhost:8098/api/leaves/export/pdf${params}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const blob = await response.blob();
      
      // Vérifier que le blob n'est pas vide
      if (blob.size === 0) {
        throw new Error('Le fichier exporté est vide');
      }

      // Vérifier le type MIME
      const contentType = response.headers.get('content-type');
      console.log('Content-Type reçu:', contentType);

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `conges_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Attendre un peu avant de nettoyer
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de l\'export PDF';
      setError(errorMessage);
      console.error('Error exporting to PDF:', err);
    }
  };

  const getMonthDays = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Jours du mois précédent
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }

    // Jours du mois actuel
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true,
      });
    }

    // Jours du mois suivant pour compléter la grille
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const getLeavesForDate = (date) => {
    return leaves.filter(leave => {
      const start = new Date(leave.startDate);
      const end = new Date(leave.endDate);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      return checkDate >= start && checkDate <= end;
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: '#ffc107',
      MANAGER_APPROVED: '#17a2b8',
      MANAGER_REJECTED: '#dc3545',
      APPROVED: '#28a745',
      REJECTED: '#dc3545',
      CANCELLED: '#6c757d',
    };
    return colors[status] || '#6c757d';
  };

  const monthDays = getMonthDays();
  const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const today = new Date();

  return (
    <div className="leave-calendar">
      <div className="calendar-header">
        <h2>Calendrier des Congés</h2>
        <div className="export-buttons">
          <button onClick={handleExportExcel} className="btn btn-excel">
            📊 Export Excel
          </button>
          <button onClick={handleExportPDF} className="btn btn-pdf">
            📄 Export PDF
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="calendar-filters">
        <div className="filter-group">
          <label>Date début:</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>Date fin:</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>Type:</label>
          <select
            value={filters.leaveType}
            onChange={(e) => setFilters({ ...filters, leaveType: e.target.value })}
          >
            <option value="ALL">Tous</option>
            <option value="ANNUAL">Congé annuel</option>
            <option value="SICK">Congé maladie</option>
            <option value="MATERNITY">Congé maternité</option>
            <option value="PATERNITY">Congé paternité</option>
            <option value="UNPAID">Sans solde</option>
            <option value="PERSONAL">Congé spéciaux</option>
            <option value="OTHER">Autre</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Statut:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="ALL">Tous</option>
            <option value="PENDING">En attente</option>
            <option value="MANAGER_APPROVED">Approuvé par manager</option>
            <option value="APPROVED">Approuvé</option>
            <option value="REJECTED">Rejeté</option>
          </select>
        </div>
        <button onClick={() => setFilters({ startDate: '', endDate: '', leaveType: 'ALL', status: 'ALL' })} className="btn btn-clear">
          Réinitialiser
        </button>
      </div>

      {loading ? (
        <div className="loading">Chargement du calendrier...</div>
      ) : (
        <div className="calendar-container">
          <div className="calendar-grid">
            {weekDays.map(day => (
              <div key={day} className="calendar-day-header">{day}</div>
            ))}
            {monthDays.map((dayInfo, index) => {
              const dayLeaves = getLeavesForDate(dayInfo.date);
              const isToday = dayInfo.date.toDateString() === today.toDateString();
              
              return (
                <div
                  key={index}
                  className={`calendar-day ${!dayInfo.isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                >
                  <div className="day-number">{dayInfo.date.getDate()}</div>
                  <div className="day-leaves">
                    {dayLeaves.map(leave => (
                      <div
                        key={leave.id}
                        className="leave-indicator"
                        style={{ backgroundColor: getStatusColor(leave.status) }}
                        title={`${leave.employeeName} - ${leave.leaveType} - ${leave.status}`}
                      >
                        {leave.employeeName.split(' ')[0]}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#ffc107' }}></span>
          <span>En attente</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#17a2b8' }}></span>
          <span>Approuvé par manager</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#28a745' }}></span>
          <span>Approuvé</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#dc3545' }}></span>
          <span>Rejeté</span>
        </div>
      </div>
    </div>
  );
};

export default LeaveCalendar;

